// Footer Banner Context & Hook
// ----------------------------
// This module implements a simple LIFO (last in, first out) stack for footer
// banner messages so that deeper components/pages can temporarily override a
// parent/global message. When an overriding component unmounts its message is
// removed, revealing the previous one beneath it.
//
// Core ideas:
// 1. Each message gets a generated id when first pushed.
// 2. Updating keeps the same stack position (no reordering or duplicate push).
// 3. Removal restores the prior message (if any) without extra bookkeeping.
// 4. The currently visible banner is always the last entry in the stack.
//
// Public hooks:
// - `useFooterBanner(message, options?)` – general-purpose; accepts severity ('info' | 'error').
// - `useFooterInfoBanner(message)` – convenience alias, always severity 'info'.
// Pass `null` (or unmount the component using the hook) to release the banner.

import {
  createContext,
  useContext,
  useMemo,
  useRef,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

export type FooterBannerSeverity = 'info' | 'error';

/**
 * One expandable detail item, rendered as a bold title followed by its (rich-text) description.
 * `description` accepts a `ReactNode` so callers can include emphasis, links, etc.
 */
export type FooterBannerDetailItem = { title: string; description: ReactNode };

type FooterEntry = {
  id: string;
  message: string;
  severity: FooterBannerSeverity;
  details?: FooterBannerDetailItem[];
};

type FooterBannerContextValue = {
  entry: FooterEntry | null;
  pushEntry: (
    message: string,
    severity: FooterBannerSeverity,
    details?: FooterBannerDetailItem[],
  ) => string;
  updateEntry: (
    id: string,
    message: string,
    severity: FooterBannerSeverity,
    details?: FooterBannerDetailItem[],
  ) => void;
  removeEntry: (id: string) => void;
};

// `description` is compared by `===`, so a non-primitive (e.g. inline JSX) description bails out
// of the fast path unless the caller keeps a stable reference (module-level constant/`useMemo`) —
// same caveat as `title`, just worth calling out now that rich content is allowed.
const areDetailItemsEqual = (
  a: FooterBannerDetailItem[] | undefined,
  b: FooterBannerDetailItem[] | undefined,
): boolean => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((item, i) => item.title === b[i].title && item.description === b[i].description);
};

const FooterBannerContext = createContext<FooterBannerContextValue | undefined>(undefined);

export const FooterInfoBannerProvider = ({ children }: { children: React.ReactNode }) => {
  const [entries, setEntries] = useState<FooterEntry[]>([]);
  const idCounterRef = useRef(0);

  // Push a new entry to the end of the stack; last entry wins.
  const pushEntry = useCallback(
    (message: string, severity: FooterBannerSeverity, details?: FooterBannerDetailItem[]) => {
      const id = `footer-${idCounterRef.current++}`;
      setEntries((prev) => [...prev, { id, message, severity, details }]);
      return id;
    },
    [],
  );

  // Update an existing entry without changing stack ordering. Bails out (returns the same
  // array reference) when nothing actually changed, so callers that pass a non-memoized
  // `details` array don't trigger a re-render on every commit.
  const updateEntry = useCallback(
    (
      id: string,
      message: string,
      severity: FooterBannerSeverity,
      details?: FooterBannerDetailItem[],
    ) => {
      setEntries((prev) => {
        const index = prev.findIndex((e) => e.id === id);
        if (index === -1) return prev;

        const existing = prev[index];
        if (
          existing.message === message &&
          existing.severity === severity &&
          areDetailItemsEqual(existing.details, details)
        ) {
          return prev;
        }

        const next = [...prev];
        next[index] = { ...existing, message, severity, details };
        return next;
      });
    },
    [],
  );

  // Remove an entry; if it was the last one the previous entry (if any) becomes visible.
  const removeEntry = useCallback((id: string) => {
    setEntries((prev) => prev.filter((e) => e.id !== id));
  }, []);

  // Derive current visible entry (top of stack) – computed each render.
  const entry = entries.length ? entries[entries.length - 1] : null;

  const value = useMemo(
    () => ({ entry, pushEntry, updateEntry, removeEntry }),
    [entry, pushEntry, updateEntry, removeEntry],
  );

  return <FooterBannerContext.Provider value={value}>{children}</FooterBannerContext.Provider>;
};

export const useFooterBannerContext = () => {
  const ctx = useContext(FooterBannerContext);

  if (!ctx) {
    throw new Error('useFooterBannerContext must be used within FooterInfoBannerProvider');
  }

  return ctx;
};

type UseFooterBannerOptions = {
  severity?: FooterBannerSeverity;
  /** Optional expandable detail items, revealed via a chevron on the banner. */
  details?: FooterBannerDetailItem[];
};

export const useFooterBanner = (
  message: string | null | undefined,
  { severity = 'info', details }: UseFooterBannerOptions = {},
) => {
  const { pushEntry, updateEntry, removeEntry } = useFooterBannerContext();
  const idRef = useRef<string | null>(null);

  // Acquire the stack entry once per "visible" session (`message` transitioning from
  // null/undefined to a real value) and release it on unmount or when it goes back to
  // null. Deliberately does NOT depend on `severity`/`details`: a `useEffect` cleanup
  // always runs between re-executions of the SAME effect (not just on unmount), so if
  // this effect depended on content values too, every render with a non-memoized
  // `details` array would remove and immediately recreate the entry under a new id
  // instead of updating it in place. Content changes are synced by the effect below via
  // `updateEntry` instead.
  useEffect(() => {
    if (message == null) return undefined;

    idRef.current = pushEntry(message, severity, details);

    return () => {
      if (idRef.current) {
        removeEntry(idRef.current);
        idRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message != null, pushEntry, removeEntry]);

  // Keep the visible entry's content in sync without recreating it. `updateEntry`
  // itself bails out (no state change) when nothing about the entry actually changed.
  useEffect(() => {
    if (message == null || !idRef.current) return;
    updateEntry(idRef.current, message, severity, details);
  }, [message, severity, details, updateEntry]);
};

/** Convenience alias — always severity 'info'. Kept for backwards compatibility. */
export const useFooterInfoBanner = (message: string | null | undefined) =>
  useFooterBanner(message, { severity: 'info' });

/** @deprecated Use `useFooterBannerContext` instead. */
export const useFooterInfoBannerContext = useFooterBannerContext;
